import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckIcon, HeartIcon } from 'lucide-react';
interface SupportModalProps {
  isOpen: boolean;
  onClose: () => void;
}
type Step = 1 | 2 | 3;
type Frequency = 'one-time' | 'monthly';
const tiers = [{
  id: 'scholar',
  name: 'Scholar',
  amount: 180,
  description: "Help bring Jabotinsky's writings to a new generation through fellowships and campus programs",
  recommended: false
}, {
  id: 'patron',
  name: 'Patron',
  amount: 500,
  description: 'Preserve and translate rare archives so his ideas remain accessible to the world',
  recommended: true
}, {
  id: 'guardian',
  name: 'Guardian',
  amount: 1800,
  description: 'Champion the Iron Wall doctrine through policy research and institutional partnerships',
  recommended: false
}];
export function SupportModal({
  isOpen,
  onClose
}: SupportModalProps) {
  const [step, setStep] = useState<Step>(1);
  const [frequency, setFrequency] = useState<Frequency>('one-time');
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setFrequency('one-time');
      setSelectedTier(null);
      setCustomAmount('');
      setName('');
      setEmail('');
      setMessage('');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  const handleNext = () => {
    if (step === 1 && (selectedTier || customAmount)) setStep(2);else if (step === 2 && name && email) setStep(3);
  };
  const handleBack = () => {
    if (step > 1) setStep(prev => prev - 1 as Step);
  };
  const getAmountDisplay = () => {
    if (customAmount) return `$${customAmount}`;
    const tier = tiers.find(t => t.id === selectedTier);
    return tier ? `$${tier.amount}` : '';
  };
  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.2
      }
    }
  };
  const contentVariants = {
    hidden: {
      opacity: 0,
      x: 20
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.4
      }
    },
    exit: {
      opacity: 0,
      x: -20,
      transition: {
        duration: 0.3
      }
    }
  };
  return <AnimatePresence data-id="element-172">
      {isOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-173">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" data-id="element-174" />

          <motion.div variants={modalVariants} initial="hidden" animate="visible" exit="exit" className="relative w-full max-w-2xl bg-[#F5F0E8] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-175">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-[#1A1A1A]/10" data-id="element-176">
              <div data-id="element-177">
                <h2 id="modal-title" className="font-serif text-2xl text-[#1A1A1A]" data-id="element-178">
                  {step === 1 && 'Select Your Contribution'}
                  {step === 2 && 'Your Information'}
                  {step === 3 && 'Thank You'}
                </h2>
                {step === 1 && <p className="text-sm text-[#1A1A1A]/50 mt-1" data-id="element-179">
                    Choose a tier or enter a custom amount
                  </p>}
              </div>
              <button onClick={onClose} className="p-2 text-[#1A1A1A]/60 hover:text-[#1A1A1A] transition-colors" aria-label="Close modal" data-id="element-180">
                <XIcon size={24} strokeWidth={1.5} data-id="element-181" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 md:p-8" data-id="element-182">
              <AnimatePresence mode="wait" data-id="element-183">
                {/* STEP 1 */}
                {step === 1 && <motion.div key="step1" variants={contentVariants} initial="hidden" animate="visible" exit="exit" className="space-y-8" data-id="element-184">
                    {/* Frequency Toggle */}
                    <div className="flex justify-center" data-id="element-185">
                      <div className="relative inline-flex bg-[#1B2A4A]/5 p-1 border border-[#1A1A1A]/10" data-id="element-186">
                        <div className="absolute top-1 bottom-1 bg-[#1B2A4A] transition-all duration-300 ease-out" style={{
                    left: frequency === 'one-time' ? '4px' : '50%',
                    width: 'calc(50% - 4px)'
                  }} data-id="element-187" />
                        <button onClick={() => setFrequency('one-time')} className={`relative z-10 px-6 py-2.5 text-sm font-medium tracking-wide transition-colors duration-300 ${frequency === 'one-time' ? 'text-[#F5F0E8]' : 'text-[#1A1A1A]/60 hover:text-[#1A1A1A]'}`} data-id="element-188">
                          One-Time
                        </button>
                        <button onClick={() => setFrequency('monthly')} className={`relative z-10 px-6 py-2.5 text-sm font-medium tracking-wide transition-colors duration-300 ${frequency === 'monthly' ? 'text-[#F5F0E8]' : 'text-[#1A1A1A]/60 hover:text-[#1A1A1A]'}`} data-id="element-189">
                          Monthly
                        </button>
                      </div>
                    </div>

                    {/* Tier Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-190">
                      {tiers.map(tier => {
                  const isSelected = selectedTier === tier.id;
                  return <button key={tier.id} onClick={() => {
                    setSelectedTier(tier.id);
                    setCustomAmount('');
                  }} className={`relative flex flex-col text-left p-5 border-2 transition-all duration-300 group ${isSelected ? 'border-[#B8860B] bg-[#B8860B]/[0.06] shadow-md' : 'border-[#1A1A1A]/10 hover:border-[#B8860B]/40 bg-white/40'}`} data-id="element-191">
                            {/* Recommended badge */}
                            {tier.recommended && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#B8860B] text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1" data-id="element-192">
                                Recommended
                              </span>}

                            {/* Selected check */}
                            {isSelected && <div className="absolute top-3 right-3 w-5 h-5 bg-[#B8860B] flex items-center justify-center" data-id="element-193">
                                <CheckIcon size={12} strokeWidth={3} className="text-white" data-id="element-194" />
                              </div>}

                            <span className="text-xs font-medium tracking-[0.15em] uppercase text-[#1A1A1A]/40 mb-2" data-id="element-195">
                              {tier.name}
                            </span>
                            <span className="text-3xl font-serif text-[#1B2A4A] mb-1" data-id="element-196">
                              ${tier.amount}
                            </span>
                            <span className="text-xs text-[#1A1A1A]/40 mb-4" data-id="element-197">
                              {frequency === 'monthly' ? 'per month' : 'one-time gift'}
                            </span>
                            <span className="text-sm text-[#1A1A1A]/60 leading-relaxed mt-auto" data-id="element-198">
                              {tier.description}
                            </span>
                          </button>;
                })}
                    </div>

                    {/* Custom Amount */}
                    <div className="relative" data-id="element-199">
                      <div className="absolute inset-x-0 top-0 flex items-center" data-id="element-200">
                        <div className="flex-1 h-px bg-[#1A1A1A]/10" data-id="element-201" />
                        <span className="px-4 text-xs text-[#1A1A1A]/40 uppercase tracking-widest" data-id="element-202">
                          or
                        </span>
                        <div className="flex-1 h-px bg-[#1A1A1A]/10" data-id="element-203" />
                      </div>
                      <div className="pt-8" data-id="element-204">
                        <div className="relative" data-id="element-205">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1A1A1A]/40 font-serif text-xl" data-id="element-206">
                            $
                          </span>
                          <input type="number" id="custom-amount" value={customAmount} onChange={e => {
                      setCustomAmount(e.target.value);
                      setSelectedTier(null);
                    }} placeholder="Enter custom amount" className={`w-full pl-9 pr-4 py-3.5 bg-white/40 border-2 focus:border-[#B8860B] focus:ring-0 outline-none transition-all font-serif text-lg text-[#1A1A1A] placeholder:text-[#1A1A1A]/25 ${customAmount ? 'border-[#B8860B] bg-[#B8860B]/[0.03]' : 'border-[#1A1A1A]/10'}`} data-id="element-207" />
                        </div>
                      </div>
                    </div>
                  </motion.div>}

                {/* STEP 2: Information */}
                {step === 2 && <motion.div key="step2" variants={contentVariants} initial="hidden" animate="visible" exit="exit" className="space-y-6" data-id="element-208">
                    <div className="bg-[#EBE5D9] p-4 border-l-2 border-[#B8860B] mb-8" data-id="element-209">
                      <p className="text-sm text-[#1A1A1A]/80" data-id="element-210">
                        You are pledging{' '}
                        <strong className="font-serif text-lg" data-id="element-211">
                          {getAmountDisplay()}
                        </strong>{' '}
                        {frequency === 'monthly' ? 'monthly' : 'as a one-time gift'}
                        .
                      </p>
                    </div>

                    <div className="space-y-4" data-id="element-212">
                      <div data-id="element-213">
                        <label htmlFor="name" className="block text-sm font-medium text-[#1A1A1A] mb-1" data-id="element-214">
                          Full Name *
                        </label>
                        <input type="text" id="name" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-3 bg-transparent border border-[#1A1A1A]/20 focus:border-[#B8860B] focus:ring-1 focus:ring-[#B8860B] outline-none transition-all text-[#1A1A1A]" required data-id="element-215" />
                      </div>
                      <div data-id="element-216">
                        <label htmlFor="email" className="block text-sm font-medium text-[#1A1A1A] mb-1" data-id="element-217">
                          Email Address *
                        </label>
                        <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 bg-transparent border border-[#1A1A1A]/20 focus:border-[#B8860B] focus:ring-1 focus:ring-[#B8860B] outline-none transition-all text-[#1A1A1A]" required data-id="element-218" />
                      </div>
                      <div data-id="element-219">
                        <label htmlFor="message" className="block text-sm font-medium text-[#1A1A1A] mb-1" data-id="element-220">
                          Dedication or Message (Optional)
                        </label>
                        <textarea id="message" value={message} onChange={e => setMessage(e.target.value)} rows={3} className="w-full px-4 py-3 bg-transparent border border-[#1A1A1A]/20 focus:border-[#B8860B] focus:ring-1 focus:ring-[#B8860B] outline-none transition-all text-[#1A1A1A] resize-none" data-id="element-221" />
                      </div>
                    </div>

                    <p className="text-xs text-[#1A1A1A]/50 italic mt-4" data-id="element-222">
                      Your information is kept private and used only for fund
                      correspondence.
                    </p>
                  </motion.div>}

                {/* STEP 3: Thank You */}
                {step === 3 && <motion.div key="step3" variants={contentVariants} initial="hidden" animate="visible" exit="exit" className="text-center py-8 space-y-8" data-id="element-223">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#B8860B]/10 text-[#B8860B] mb-4" data-id="element-224">
                      <CheckIcon size={32} strokeWidth={1.5} data-id="element-225" />
                    </div>
                    <div data-id="element-226">
                      <h3 className="text-3xl font-serif text-[#1A1A1A] mb-2" data-id="element-227">
                        Your pledge has been received
                      </h3>
                      <p className="text-[#1A1A1A]/70" data-id="element-228">
                        Thank you, {name}. We have recorded your intended
                        contribution of{' '}
                        <strong className="font-serif text-lg" data-id="element-229">
                          {getAmountDisplay()}
                        </strong>
                        .
                      </p>
                    </div>
                    <blockquote className="max-w-md mx-auto text-xl text-[#1B2A4A] font-serif italic border-l-2 border-[#B8860B] pl-6 py-2 text-left" data-id="element-230">
                      "The power of a people is measured not by its wealth, but
                      by its will."
                    </blockquote>
                    <p className="text-sm text-[#1A1A1A]/60 max-w-sm mx-auto" data-id="element-231">
                      A member of our team will be in touch shortly at {email}{' '}
                      to complete your contribution securely.
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-[#1A1A1A]/10 bg-[#EBE5D9]/50 flex justify-between items-center" data-id="element-232">
              {step === 1 && <>
                  <div className="text-sm text-[#1A1A1A]/50" data-id="element-233">Step 1 of 2</div>
                  <button onClick={handleNext} disabled={!selectedTier && !customAmount} className="px-8 py-3 bg-[#1B2A4A] text-[#F5F0E8] font-medium tracking-wide hover:bg-[#1B2A4A]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-234">
                    Continue
                  </button>
                </>}
              {step === 2 && <>
                  <button onClick={handleBack} className="px-6 py-3 text-[#1A1A1A]/70 hover:text-[#1A1A1A] font-medium tracking-wide transition-colors" data-id="element-235">
                    Back
                  </button>
                  <button onClick={handleNext} disabled={!name || !email} className="px-8 py-3 bg-[#1B2A4A] text-[#F5F0E8] font-medium tracking-wide hover:bg-[#1B2A4A]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-236">
                    Complete Pledge
                  </button>
                </>}
              {step === 3 && <div className="w-full flex justify-center" data-id="element-237">
                  <button onClick={onClose} className="px-8 py-3 border border-[#1B2A4A] text-[#1B2A4A] font-medium tracking-wide hover:bg-[#1B2A4A]/5 transition-colors w-full sm:w-auto" data-id="element-238">
                    Close & Return
                  </button>
                </div>}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}